// packages/storefront/src/api/chat.ts
//
// The browser-side client for the anonymous website-visitor chat endpoints
// (apps/api /v1/businesses/{bizId}/chat/...). Thin wrappers over publicApiRequest:
// bizToken in Authorization, the per-thread Chat-Session-Token in its header. The
// receive leg is a JSON poll (fetchSince) — SSE is the later swap behind the same
// shape, with no widget change.

import type { Result } from '@tourtask/types/result';
import type { ChatThread, ChatMessage } from '@tourtask/chat-domain';
import type { CountryWithName } from '@tourtask/types/configs/Country';
import type { WidgetBrandColors } from '@tourtask/types/api/v1/WidgetBrandColors';
import { publicApiRequest } from './publicApiRequest';

/** What the widget should render for this business (resolved server-side from the
 *  frozen-account state, the AI + human support toggles, and whether a contact
 *  email exists). Mirrors `WidgetChatMode` in apps/api/src/lib/chat/widgetMode.ts —
 *  embed can't import from apps/api, so the union is duplicated here; keep in sync. */
export type WidgetChatMode = 'chat' | 'email_only' | 'hidden' | 'frozen';

export interface ChatReference {
  /** 'chat' = live conversation; 'email_only' = leave-a-message form; 'hidden' =
   *  don't render the widget; 'frozen' = account frozen for unpaid invoices, widget
   *  disabled with a notice. */
  mode: WidgetChatMode;
  /** Whether the AI bot is on — drives the presence chip (green "Online" only when
   *  a human is truly available; no "offline" chip when the bot covers the chat). */
  aiEnabled: boolean;
  /** In 'chat' mode: whether the visitor gives name, email and phone before the
   *  first message (the pre-chat form) or writes straight away (the business's
   *  "Ask for contact details first" setting). Absent from an API older than the
   *  setting — the bundle deploys before the API — which means ask, as then. */
  askContactDetails?: boolean;
  countries: CountryWithName[];
  /**
   * The business's brand (docs/brand-colours.md phase 4): MUI's primary and the panel header's band. Absent from
   * an API older than it (the bundle deploys first): MUI's own colours, as before.
   */
  brand?: WidgetBrandColors;
}

/** Widget bootstrap: the mode + the localized country list for the pre-chat /
 *  leave-a-message phone field. Lightweight (no catalog, no service-area gate).
 *  Fetched once at mount so the widget knows whether/how to render before showing
 *  the launcher. The phone country is never preselected — the visitor (typically a
 *  tourist) picks their own — so no business country is sent. */
export function fetchChatReference(args: {
  bizSlug: string;
  bizToken: string;
  lang: string;
}): Promise<Result<ChatReference>> {
  return publicApiRequest<ChatReference>({
    path: `businesses/${encodeURIComponent(args.bizSlug)}/chat/reference?lang=${encodeURIComponent(args.lang)}`,
    bizToken: args.bizToken,
  });
}

// The bot's turn runs server-side AFTER the start/send response (the reply
// arrives via the poll, under the typing indicator), so these calls normally
// return fast. The raised timeout is deploy-skew insurance: against an older
// API that still runs the AI turn (~20s with tools) inside the request, the
// default 15s abort left the visitor with no session and a re-enabled form —
// every re-click minted a duplicate thread. Matches the API's old 60s budget.
const CHAT_TURN_TIMEOUT_MS = 65_000;

export interface StartChatResult {
  sessionToken: string;
  thread: ChatThread;
  messages: ChatMessage[];
  supportOnline: boolean;
}
export interface SendChatResult {
  /** The visitor's own message, echoed back to collapse the optimistic placeholder. */
  message: ChatMessage;
  /** The bot's reply — EMPTY on the current API (the turn runs after the
   *  response; the reply arrives via the poll). Still merged when present so an
   *  older API that answers in-request keeps working during deploy skew. */
  replies: ChatMessage[];
}
export interface PollChatResult {
  thread: ChatThread;
  messages: ChatMessage[];
  supportOnline: boolean;
}

/** Start a session: create the thread + persist the visitor's first message;
 *  returns the Chat-Session-Token the widget stores to resume the same thread.
 *  The contact details are left out for a business that does not ask for them
 *  first (`ChatReference.askContactDetails`). */
export function startChatThread(args: {
  bizSlug: string;
  bizToken: string;
  visitorName?: string;
  visitorEmail?: string;
  /** ISO country code (CHAR(2)) + local number, stored separately server-side. */
  visitorPhoneCountryCode?: string;
  visitorPhoneNumber?: string;
  firstMessage: string;
  clientMsgId: string;
  turnstileToken?: string;
  /** Widget `data-lang` — the bot replies + confirmation emails use this. */
  locale?: string;
  /** Persistent per-visitor id (abuse keying) — survives across pages/sessions. */
  visitorId?: string;
}): Promise<Result<StartChatResult>> {
  return publicApiRequest<StartChatResult>({
    path: `businesses/${encodeURIComponent(args.bizSlug)}/chat/sessions`,
    method: 'POST',
    body: {
      visitorName: args.visitorName,
      visitorEmail: args.visitorEmail,
      visitorPhoneCountryCode: args.visitorPhoneCountryCode,
      visitorPhoneNumber: args.visitorPhoneNumber,
      firstMessage: args.firstMessage,
      clientMsgId: args.clientMsgId,
      locale: args.locale,
      visitorId: args.visitorId,
    },
    bizToken: args.bizToken,
    turnstileToken: args.turnstileToken,
    timeoutMs: CHAT_TURN_TIMEOUT_MS,
  });
}

/** Send a visitor message into an existing session. */
export function sendChatMessage(args: {
  bizSlug: string;
  bizToken: string;
  threadId: number;
  sessionToken: string;
  body: string;
  clientMsgId: string;
  /** Widget `data-lang` — the bot replies in this language. */
  locale?: string;
  /** Persistent per-visitor id (abuse keying). */
  visitorId?: string;
}): Promise<Result<SendChatResult>> {
  return publicApiRequest<SendChatResult>({
    path: `businesses/${encodeURIComponent(args.bizSlug)}/chat/sessions/${args.threadId}/messages`,
    method: 'POST',
    body: { body: args.body, clientMsgId: args.clientMsgId, locale: args.locale, visitorId: args.visitorId },
    bizToken: args.bizToken,
    chatSessionToken: args.sessionToken,
    timeoutMs: CHAT_TURN_TIMEOUT_MS,
  });
}

/** Report the visitor's read position (highest message id seen with the panel
 *  open) so the business console can show per-message read receipts on its
 *  replies. Fire-and-forget from the widget's perspective — a lost report just
 *  delays the receipt until the next one. */
export function markChatRead(args: {
  bizSlug: string;
  bizToken: string;
  threadId: number;
  sessionToken: string;
  lastReadMessageId: number;
}): Promise<Result<{ ok: true }>> {
  return publicApiRequest<{ ok: true }>({
    path: `businesses/${encodeURIComponent(args.bizSlug)}/chat/sessions/${args.threadId}/read`,
    method: 'POST',
    body: { lastReadMessageId: args.lastReadMessageId },
    bizToken: args.bizToken,
    chatSessionToken: args.sessionToken,
  });
}

/** Poll a session for the message delta since `sinceSeq`, plus thread state +
 *  whether the business is online (the green dot). */
export function pollChatThread(args: {
  bizSlug: string;
  bizToken: string;
  threadId: number;
  sessionToken: string;
  sinceSeq: number;
}): Promise<Result<PollChatResult>> {
  return publicApiRequest<PollChatResult>({
    path: `businesses/${encodeURIComponent(args.bizSlug)}/chat/sessions/${args.threadId}?sinceSeq=${args.sinceSeq}`,
    method: 'GET',
    bizToken: args.bizToken,
    chatSessionToken: args.sessionToken,
  });
}
